import { decodeJwt } from "jose";

/**
 * Auth for the admin SPA — mirrors ARK's pattern. Google sign-in (auth-code popup, hosted-domain
 * restricted) yields a code that a backend exchanges for an ID token; the gateway then validates
 * that token on every API call. We store the token, attach it as `Authorization: Bearer`, and
 * refresh on 401. The pure `validateIdToken` below is the testable nucleus.
 */
export interface AuthData {
  idToken?: string | undefined;
  refreshToken?: string | undefined;
  /** Unix ms expiry (stored as a number for JSON round-tripping). */
  expiryMs?: number | undefined;
  email?: string | undefined;
}

export interface TokenResponse {
  id_token?: string;
  refresh_token?: string;
  expiry_date?: string | number;
}

export interface TokenValidation {
  valid: boolean;
  reason?: string;
}

/**
 * Validate the stored token: not expired, in the allowed domain, and — for Google's tokens —
 * email verified.
 *
 * TWO token shapes reach this, and both must pass:
 *   - Google's ID token, when auth runs AUTH_TOKEN_MODE=google.
 *   - Our own ES256 access token (`kind: "user"`), when auth runs AUTH_TOKEN_MODE=own. That is
 *     what /auth/admin/access-token returns as `id_token` today; auth deliberately reuses the
 *     field so un-migrated clients keep sending a token the gateway accepts.
 *
 * AUTH_TOKEN_MODE is the documented cutover rollback lever, so this keeps accepting Google's
 * shape rather than swapping wholesale to ours — a rollback must not lock everyone out.
 *
 * Our token carries no `email_verified`: Google verified the address and the staff directory
 * then permitted the principal, BOTH before minting. Demanding the claim here rejected every
 * own-mode login — the SPA sat on the login screen despite a 200 from the exchange. Google's
 * shape keeps the strict allow-list below, where an absent claim is still a rejection.
 */
export function validateIdToken(
  idToken: string | undefined,
  allowedDomain: string,
  nowSeconds: number,
): TokenValidation {
  if (!idToken) return { valid: false, reason: "missing token" };
  let claims: Record<string, unknown>;
  try {
    claims = decodeJwt(idToken) as Record<string, unknown>;
  } catch {
    return { valid: false, reason: "undecodable token" };
  }
  // Strict allow-lists, not deny-value checks: a missing/non-numeric exp must NOT read as
  // never-expiring, and email_verified must be explicitly truthy (Google may stringify it).
  if (typeof claims.exp !== "number" || claims.exp <= nowSeconds) {
    return { valid: false, reason: "token expired" };
  }
  const isOwnUserToken = claims.kind === "user";
  if (!isOwnUserToken && !(claims.email_verified === true || claims.email_verified === "true")) {
    return { valid: false, reason: "email not verified" };
  }
  const email = typeof claims.email === "string" ? claims.email : undefined;
  const hd = typeof claims.hd === "string" ? claims.hd : email?.split("@")[1];
  if (hd !== allowedDomain) {
    return { valid: false, reason: `not a ${allowedDomain} account` };
  }
  return { valid: true };
}

const STORAGE_KEY = `${import.meta.env.VITE_ENV}.ringswap.admin.auth`;
const REFRESH_SKEW_MS = 60_000; // refresh a minute before expiry

export class AuthService {
  private data: AuthData = {};
  /** In-tab single-flight guard for refresh() — see the comment there. */
  private inFlightRefresh: Promise<string | undefined> | null = null;

  constructor(
    private readonly apiBase: string,
    private readonly allowedDomain: string,
  ) {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      try {
        this.data = JSON.parse(raw) as AuthData;
      } catch {
        this.data = {};
      }
    }
  }

  get email(): string | undefined {
    return this.data.email;
  }

  isAuthenticated(): boolean {
    return validateIdToken(this.data.idToken, this.allowedDomain, Math.floor(Date.now() / 1000)).valid;
  }

  /** Exchange the OAuth auth-code for tokens (backend does the confidential exchange). */
  async handleLoginSuccess(code: string): Promise<void> {
    const res = await fetch(`${this.apiBase}/auth/admin/access-token`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ code }),
    });
    if (!res.ok) throw new Error(`token exchange failed: HTTP ${res.status}`);
    this.store((await res.json()) as TokenResponse);
  }

  logout(): void {
    this.data = {};
    localStorage.removeItem(STORAGE_KEY);
  }

  /** Fetch with a valid Bearer token, refreshing once on 401. */
  async authFetch(path: string, options: RequestInit = {}): Promise<Response> {
    const token = await this.getValidToken();
    if (!token) throw new Error("not authenticated");
    const headers = { ...(options.headers as Record<string, string>), Authorization: `Bearer ${token}` };
    const res = await fetch(`${this.apiBase}${path}`, { ...options, headers });
    if (res.status === 401) {
      const refreshed = await this.refresh();
      if (refreshed) {
        return fetch(`${this.apiBase}${path}`, {
          ...options,
          headers: { ...headers, Authorization: `Bearer ${refreshed}` },
        });
      }
    }
    return res;
  }

  private async getValidToken(): Promise<string | undefined> {
    if (this.data.expiryMs && this.data.expiryMs - REFRESH_SKEW_MS < Date.now()) {
      await this.refresh();
    }
    return this.data.idToken;
  }

  /**
   * Refresh tokens are single-use. Auth rotates the presented token and reads a SECOND
   * presentation of the same value as theft — it revokes the whole token family and ends
   * every session that user has (session.reuse_detected). So a refresh must happen exactly
   * once per token, and there are two ways to fire it twice:
   *
   *   - Within this tab: getValidToken() and the 401 branch of authFetch both call refresh(),
   *     so concurrent API calls each started their own. `inFlight` collapses them into one.
   *   - Across tabs: each tab decides to refresh from the same stored expiry, so two open
   *     tabs fire within milliseconds of each other. Web Locks serialise them, and the loser
   *     adopts the winner's token from storage rather than presenting one already rotated.
   *
   * Under AUTH_TOKEN_MODE=google this was invisible: Google's refresh tokens are reusable.
   * Our own tokens rotate, which is what turned a second tab into a random logout.
   */
  private async refresh(): Promise<string | undefined> {
    if (!this.data.refreshToken) return undefined;
    if (this.inFlightRefresh) return this.inFlightRefresh;
    const attempt = this.refreshUnderCrossTabLock();
    this.inFlightRefresh = attempt;
    try {
      return await attempt;
    } finally {
      if (this.inFlightRefresh === attempt) this.inFlightRefresh = null;
    }
  }

  private async refreshUnderCrossTabLock(): Promise<string | undefined> {
    const locks = globalThis.navigator?.locks;
    // No Web Locks (older Safari, insecure context, jsdom): degrade to a plain refresh
    // rather than failing — the in-tab guard above still applies.
    if (!locks) return this.executeRefresh();
    return locks.request(`ringswap-admin-refresh-${import.meta.env.VITE_ENV}`, async () => {
      const adopted = this.adoptTokenFromOtherTab();
      return adopted ?? this.executeRefresh();
    });
  }

  /**
   * Runs while holding the lock: if another tab rotated while we queued, its token is
   * already in storage and ours is spent. Adopt theirs. Returns undefined when nothing
   * moved and this tab should do the refresh itself.
   */
  private adoptTokenFromOtherTab(): string | undefined {
    const presented = this.data.refreshToken;
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return undefined;
      const stored = JSON.parse(raw) as AuthData;
      if (!stored.refreshToken || stored.refreshToken === presented) return undefined;
      this.data = stored;
      return stored.idToken;
    } catch {
      return undefined;
    }
  }

  private async executeRefresh(): Promise<string | undefined> {
    const res = await fetch(`${this.apiBase}/auth/admin/refresh-token`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ refreshToken: this.data.refreshToken }),
    });
    if (!res.ok) {
      this.logout();
      return undefined;
    }
    this.store((await res.json()) as TokenResponse);
    return this.data.idToken;
  }

  private store(resp: TokenResponse): void {
    const idToken = resp.id_token;
    const claims = idToken ? (decodeJwt(idToken) as Record<string, unknown>) : {};
    this.data = {
      idToken,
      refreshToken: resp.refresh_token ?? this.data.refreshToken,
      expiryMs: resp.expiry_date ? Number(resp.expiry_date) : undefined,
      email: typeof claims.email === "string" ? claims.email : undefined,
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(this.data));
  }
}
